// Interactive Registry
import { makeInt, makeString } from './skills';
import * as Interaction from '../interaction';
import utils from '../utils';
import type InteractivePrototype from '../uTheory.Interaction/interactivePrototype';

type SkillId = number | string;

export interface InteractiveRegistryEntry<T = any> {
  description: string;
  skills: SkillId[];
  alsoTrains?: SkillId[];
  options?: T;
  /**
   * Indicates that this exercise knows it is not suitable for training a particular
   * set of matched exercise skill sets. Skills array must be an exact match (set equals).
   */
  excludeExerciseForMatchedSkillSets?: SkillId[][];
  /**
   * Maybe the current set of skills isn't valid for this config... e.g., an ID
   * question with only asc_M2_et listed.
   */
  testForSkillSetValidity?: (skillIds: string[]) => boolean;
}

interface InteractiveRegistryEntryWithName<T = any> extends InteractiveRegistryEntry<T> {
  name: string;
  interactive: InteractivePrototype;
}

const isNumber = (n: any): n is number => typeof n === 'number';

const interactiveNames = Object.keys(Interaction);
const interactiveRegistry: InteractiveRegistryEntryWithName[] = interactiveNames.reduce(
  (arr, name) => {
    const interactive = Interaction[name];
    if (!interactive.hasOwnProperty('registry')) return arr;
    // cast as array, if not already:
    const registryEntries: InteractiveRegistryEntry[] = [].concat(interactive.registry);

    const registryEntriesWithName = registryEntries.map((register) => {
      if (!register.skills)
        throw Error(`${name} does not have skills defined on its registry entry.`);
      return {
        ...register,
        name,
        interactive,
        skills: [].concat(register.skills) as (string | number)[],
      };
    });
    return arr.concat(registryEntriesWithName);
  },
  []
);

export const suggestExercises = (skillOrSkills: string | number | (string | number)[]) => {
  //  Returns an array of exercises as objects:
  //  exercises - [
  //  {
  //      interactive: interactive
  //      name: InteractiveName
  //      skills: [array of skills trained]
  //      options: suggested options to pass as 2nd argument to the interactive.
  //  }
  //  ]
  //
  //  Initialize exercise with:
  //      var interactive = new exercise.interactive(displayDiv, exercise.options);
  //  or with:
  //      var interactive = new Interaction[exercise.name](displayDiv, exercise.options);

  if (!(skillOrSkills instanceof Array)) skillOrSkills = [skillOrSkills];
  const exercises: {
    interactive: InteractivePrototype;
    description: string;
    name: string;
    skills: number[];
    options: {
      skills: number[];
    };
  }[] = [];

  // Be sure all are in integer format and actually exist:
  const skillsArray = skillOrSkills.map(makeInt).filter(isNumber);

  // forEach interactive:
  interactiveRegistry.forEach((register) => {
    // console.log(`Checking interactive ${register.name}`);
    let alsoTrains = [];

    // which skills are matched?
    const matchedSkills = register.skills
      .map(makeInt)
      .filter(isNumber)
      .filter((skill) => {
        if (typeof skill !== 'number') return false;
        const intId = makeInt(skill);
        return !!intId && skillsArray.indexOf(intId) !== -1;
      });

    // Is this exact set of matched skills on the registry's exclude list?
    // If so, unmatch the skills.
    const excluded = !!register.excludeExerciseForMatchedSkillSets?.find((excludedSet) => {
      const excludedIds = excludedSet.map(makeInt).filter(isNumber);
      return utils.arrayContentsEqual(matchedSkills, excludedIds);
    });

    const valid = register.testForSkillSetValidity
      ? register.testForSkillSetValidity(matchedSkills.map(makeString))
      : true;

    // any skills also trained, but not primary?
    if (typeof register.alsoTrains !== 'undefined') {
      alsoTrains = register.alsoTrains.filter(
        (skill) => skillsArray.indexOf(makeInt(skill) as number) !== -1
      );
    }

    // create a new object, setting skills
    if (!excluded && valid && matchedSkills.length > 0) {
      const options = { skills: matchedSkills.concat(alsoTrains) };
      utils.merge(options, register.options || {});
      const exercise = {
        interactive: register.interactive,
        description: register.description,
        name: register.name,
        skills: matchedSkills.concat(alsoTrains),
        options,
      };
      // add object to exercises
      exercises.push(exercise);
    }
  });

  // console.log('Matched exercises', exercises);
  return exercises;
};

export const hasExercise = (skill) => {
  const intSkill = makeInt(skill);
  return !!interactiveRegistry.find((register) =>
    register.skills
      .map(makeInt)
      .filter((s) => s !== undefined)
      .includes(intSkill)
  );
};
